import React, { useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import ClientPortal from '../components/projects/ClientPortal';
import './ClientPortalPage.css';

const ClientPortalPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { timberProjects, projectTasks, milestones, loading: crmLoading } = useCRM();
  useCRMCollections(['projectTasks', 'milestones']);

  // Find the project
  const project = useMemo(() => {
    return timberProjects.find(p => p.id === projectId);
  }, [timberProjects, projectId]);

  // Get tasks and milestones for this project
  const projectTaskList = useMemo(() => {
    return projectTasks.filter(t => t.projectId === projectId);
  }, [projectTasks, projectId]);

  const projectMilestoneList = useMemo(() => {
    return milestones.filter(m => m.projectId === projectId);
  }, [milestones, projectId]);

  // Loading state
  if (authLoading || crmLoading) {
    return (
      <div className="portal-page-loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Auth check - clients can only view their own projects
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Project not found
  if (!project) {
    return (
      <div className="portal-page-error">
        <div className="error-content">
          <h2>Project Not Found</h2>
          <p>The project you're looking for doesn't exist or you don't have access to it.</p>
          <a href="/" className="back-link">Return to Dashboard</a>
        </div>
      </div>
    );
  }

  // Check access - clients can only see their own projects
  if (user?.role === 'client' && project.clientEmail !== user.email) {
    return (
      <div className="portal-page-error">
        <div className="error-content">
          <h2>Access Denied</h2>
          <p>You don't have permission to view this project.</p>
          <a href="/" className="back-link">Return to Dashboard</a>
        </div>
      </div>
    );
  }

  return (
    <div className="client-portal-page">
      <ClientPortal
        project={project}
        milestones={projectMilestoneList}
        tasks={projectTaskList}
      />
    </div>
  );
};

export default ClientPortalPage;
